import { useEffect, useLayoutEffect, useState } from 'react';
import {
  BUBBLE_W,
  BUDDY_TOKENS,
  CHEV_HH,
  CHEV_HW,
  CHEV_SW,
  CORE_R,
  DEFAULT_LINE,
  EYE_L_X,
  EYE_R_X,
  EYE_RX,
  EYE_RY,
  EYE_Y,
  FACES,
  HALO_R,
  ROUTE_LINES,
} from './constants';

export const lineForPath = (path: string) => {
  const normalized = !path || path === '/' ? '/chat' : path;
  const found = ROUTE_LINES
    .filter((line) => normalized === line.path || normalized.startsWith(line.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return found || DEFAULT_LINE;
};

export const isConversationPath = (pathname: string): boolean =>
  /^\/(chat|agentChat)\/[^/]+/.test(pathname || '');

export const caretClientX = (field: HTMLTextAreaElement | HTMLInputElement): number => {
  const index = typeof field.selectionStart === 'number' ? field.selectionStart : field.value.length;
  const style = window.getComputedStyle(field);
  const mirror = document.createElement('div');
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.overflowWrap = 'break-word';
  mirror.style.font = style.font;
  mirror.style.letterSpacing = style.letterSpacing;
  mirror.style.padding = style.padding;
  mirror.style.border = style.border;
  mirror.style.boxSizing = style.boxSizing;
  mirror.style.width = style.width;
  mirror.textContent = field.value.slice(0, index);
  const mark = document.createElement('span');
  mark.textContent = '\u200b';
  mirror.appendChild(mark);
  document.body.appendChild(mirror);
  const host = field.getBoundingClientRect();
  const base = mirror.getBoundingClientRect();
  const markBox = mark.getBoundingClientRect();
  mirror.remove();
  return host.left + (markBox.left - base.left) - field.scrollLeft;
};

export const isConversationView = (): boolean => {
  if (typeof window !== 'undefined' && isConversationPath(window.location.pathname)) return true;
  if (typeof document === 'undefined') return false;
  const column = document.querySelector('[data-testid="chat-page-column"]');
  return !!column && !column.querySelector('[data-testid="empty-chat-top-spacer"]');
};

export const bubbleSideFor = (centerX: number, faceSize: number): 'left' | 'right' => {
  const half = (faceSize || 102) / 2;
  const roomRight = (window.innerWidth || 1024) - (centerX + half) - 20;
  const roomLeft = centerX - half - 20;
  return roomRight >= BUBBLE_W || roomRight >= roomLeft ? 'right' : 'left';
};

export const chevronRight = (cx: number): string =>
  `M ${cx - CHEV_HW} ${EYE_Y - CHEV_HH} L ${cx + CHEV_HW} ${EYE_Y} L ${cx - CHEV_HW} ${EYE_Y + CHEV_HH}`;

export const chevronLeft = (cx: number): string =>
  `M ${cx + CHEV_HW} ${EYE_Y - CHEV_HH} L ${cx - CHEV_HW} ${EYE_Y} L ${cx + CHEV_HW} ${EYE_Y + CHEV_HH}`;

export const chevronDown = (cx: number): string =>
  `M ${cx - CHEV_HW} ${EYE_Y - CHEV_HH * 0.35} L ${cx} ${EYE_Y + CHEV_HH} L ${cx + CHEV_HW} ${EYE_Y - CHEV_HH * 0.35}`;

export const heart = (cx: number): string => {
  const s = EYE_RY * 0.9;
  const y = EYE_Y + s * 0.15;
  return `M ${cx} ${y + s * 0.55} C ${cx - s * 1.05} ${y - s * 0.15} ${cx - s * 0.55} ${y - s} ${cx} ${y - s * 0.28} C ${cx + s * 0.55} ${y - s} ${cx + s * 1.05} ${y - s * 0.15} ${cx} ${y + s * 0.55} Z`;
};

export const usePathname = (): string => {
  const [path, setPath] = useState(() => (typeof window === 'undefined' ? '/' : window.location.pathname));
  useEffect(() => {
    const sync = () => setPath(window.location.pathname);
    const push = history.pushState.bind(history);
    const replace = history.replaceState.bind(history);
    history.pushState = (...args: Parameters<History['pushState']>) => {
      push(...args);
      sync();
    };
    history.replaceState = (...args: Parameters<History['replaceState']>) => {
      replace(...args);
      sync();
    };
    window.addEventListener('popstate', sync);
    return () => {
      history.pushState = push;
      history.replaceState = replace;
      window.removeEventListener('popstate', sync);
    };
  }, []);
  return path;
};

export const useMediaQuery = (query: string): boolean => {
  const [match, setMatch] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
};

export const useInjectedStyles = (css: string): void => {
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    let el = document.querySelector('style[data-klax-buddy]');
    if (!el) {
      el = document.createElement('style');
      el.setAttribute('data-klax-buddy', '');
      document.head.appendChild(el);
    }
    el.textContent = css;
  }, [css]);
};

type EyeProps = { kind: string; side: 'left' | 'right' };

export function Eye({ kind, side }: EyeProps) {
  const cx = side === 'left' ? EYE_L_X : EYE_R_X;
  if (kind === 'dot' || kind === 'wide' || kind === 'flat') {
    const rx = kind === 'flat' ? EYE_RX * 1.15 : kind === 'wide' ? EYE_RX * 1.25 : EYE_RX;
    const ry = kind === 'flat' ? EYE_RY * 0.38 : kind === 'wide' ? EYE_RY * 1.35 : EYE_RY;
    return <ellipse cx={cx} cy={EYE_Y} rx={rx} ry={ry} fill={BUDDY_TOKENS.face} />;
  }
  if (kind === 'heart') {
    return <path d={heart(cx)} fill={BUDDY_TOKENS.face} />;
  }
  const d =
    kind === 'chevronLeft' ? chevronLeft(cx) : kind === 'chevronDown' ? chevronDown(cx) : chevronRight(cx);
  return (
    <path
      d={d}
      fill="none"
      stroke={BUDDY_TOKENS.face}
      strokeWidth={CHEV_SW}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

type FaceName = keyof typeof FACES;

type BuddyFaceProps = { expression?: FaceName; haloId?: string };

/** Static face. Useful for docs, reference rows and favicons. */
export function BuddyFace({ expression = 'neutral', haloId = 'klaxHaloStatic' }: BuddyFaceProps) {
  const face = FACES[expression];
  return (
    <svg viewBox="0 0 200 200" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={haloId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={BUDDY_TOKENS.accent} stopOpacity="0.55" />
          <stop offset="60%" stopColor={BUDDY_TOKENS.accent} stopOpacity="0.32" />
          <stop offset="100%" stopColor={BUDDY_TOKENS.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r={HALO_R} fill={`url(#${haloId})`} />
      <circle cx="100" cy="100" r={CORE_R} fill={BUDDY_TOKENS.accent} />
      <Eye kind={face.left} side="left" />
      <Eye kind={face.right} side="right" />
    </svg>
  );
}
